import { lighten, makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import {
  AssignmentTurnedIn,
  Business,
  MonetizationOn,
  Person,
  Room, LocalOffer
} from '@material-ui/icons';
import { BrokerInformation, LoanStore, LoanSubmissionStep } from '@roc/feature-loans';
import { Layout } from '@roc/feature-app-core';
import { insertIf, LoanSubType } from '@roc/feature-utils';
import { Paper, StepsContainer } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';

import BorrowerEntity from 'libs/feature-loans/src/loanSubmission/components/entityInformation';
import { useRoutes, useStore } from '@roc/client-portal-shared/hooks';
import PropertyInformationStbBridge from 'libs/feature-loans/src/loanSubmission/stabilizedBridge/propertyInformationStbBridge';
import { PropertyStepsStabilizedBridge } from 'libs/feature-loans/src/loanSubmission/components/propertyStepsStabilizedBridge';
import LoanInformationStabilizedBridge from 'libs/feature-loans/src/loanSubmission/stabilizedBridge/components/loanInformationStabilizedBridge';
import BorrowerInformation from 'libs/feature-loans/src/loanSubmission/components/borrowerInformationStbBridge';
import Summary from 'libs/feature-loans/src/loanSubmission/stores/stabilizedBridge/summary';
import { SelectPromotionGrid } from '@roc/feature-loans';


const useStyles = makeStyles(theme =>
  createStyles({
    container: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    paper: {
      padding: theme.spacing(4),
    },
    termSheetContainer: {
      padding: theme.spacing(2),
      marginBottom: '24px',
      backgroundColor: lighten(theme.palette.primary.main, 0.8),
    },
    termSheetDownload: {
      display: 'flex',
      alignItems: 'center',
    }
  })
);

export interface EditStabilizedBridgeProps {
  loanStore: LoanStore;
}

export const EditStabilizedBridge = observer(() => {
  const { stabilizedBridgeStore, globalStore } = useStore();
  const { loanId } = useParams<{ loanId: string }>();
  const classes = useStyles();
  const { loanRoutesConfig } = useRoutes();
  const { push } = useHistory();
  const showBrokerInformation = !globalStore.userFeatures.isBroker;
  const { marketingPromotionsStore } = stabilizedBridgeStore;

  useEffect(() => {
    stabilizedBridgeStore.loadStore(loanId);
    stabilizedBridgeStore.setLoanType(LoanSubType.STABILIZED_BRIDGE);
    stabilizedBridgeStore.selectBorrowersStore.setAllowMultiple(false);
    marketingPromotionsStore.fetchGridData();
  }, [loanId]);

  const onSubmit = () => {
    const isLoanDetailsValid =
      stabilizedBridgeStore.loanInformationStore.form.meta.isValid;
    const isAllStepsValid = stabilizedBridgeStore.allErrors.length === 0;
    if (!isLoanDetailsValid) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please correct the errors in Loan Details',
      });
    } else if (stabilizedBridgeStore.allErrors.length > 0) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please correct the errors listed above',
      });
    }

    const isValid = isAllStepsValid && isLoanDetailsValid;
    if (isValid) {
      stabilizedBridgeStore.onEditLoanSubmit(() => push(loanRoutesConfig.loans(stabilizedBridgeStore.editLoanJson.loanId).children.dashboard.url))
    }
  };

  const goNextStep = () => {
    const currentStep = loanSubmissionSteps[stabilizedBridgeStore.activeStep].type;
    stabilizedBridgeStore.goNextStep(currentStep);
  }

  const goPrevStep = () => {
    const currentStep = loanSubmissionSteps[stabilizedBridgeStore.activeStep].type;
    stabilizedBridgeStore.goPrevStep(currentStep);
  }

  const moveToStep = (stepIndex) => {
    const currentStep = loanSubmissionSteps[stabilizedBridgeStore.activeStep].type;
    stabilizedBridgeStore.moveToStep(stepIndex, currentStep);
  }

  const setPromotion = (promotionId: number) => {
    stabilizedBridgeStore.loanInformationStore.onChange('marketingPromotionId', promotionId);
  }


  const loanSubmissionSteps = [
    ...insertIf(showBrokerInformation, [{
      title: 'Broker Information',
      icon: <Person color="primary" fontSize="large" />,
      type: LoanSubmissionStep.BROKER_INFORMATION,
      onBack: () => {
        push(loanRoutesConfig.loans(stabilizedBridgeStore.editLoanJson.loanId).children.dashboard.url);
      },
      onNext: () => goNextStep(),
      allowBack: true,
      allowNext: true,
    }]),
    {
      title: 'Entity Information',
      icon: <Business color="primary" fontSize="large" />,
      type: LoanSubmissionStep.ENTITY_INFORMATION,
      onBack: () => {
        goPrevStep();
      },
      onNext: () => {
        goNextStep();
      },
      allowBack: true,
      allowNext: true,
    },
    {
      title: 'Borrower Information',
      icon: <Person color="primary" fontSize="large" />,
      type: LoanSubmissionStep.BORROWER_INFORMATION,
      onBack: () => goPrevStep(),
      onNext: () => goNextStep(),
      allowBack: true,
      allowNext: true,
    },
    {
      title: 'Property Information',
      icon: <Room color="primary" fontSize="large" />,
      type: LoanSubmissionStep.PROPERTY_INFORMATION,
      onBack: () => goPrevStep(),
      onNext: () => goNextStep(),
      allowBack: true,
      allowNext: true,
    },
    {
      title: 'Loan Details',
      icon: <MonetizationOn color="primary" fontSize="large" />,
      type: LoanSubmissionStep.LOAN_DETAILS,
      onBack: () => goPrevStep(),
      onNext: () => {
        goNextStep()
      },
      allowBack: true,
      allowNext: true,
    },
    ...insertIf(marketingPromotionsStore.showPromotions, [{
      title: 'Promotions',
      icon: <LocalOffer color="primary" fontSize="large" />,
      type: LoanSubmissionStep.PROMOTIONS,
      onBack: () => goPrevStep(),
      onNext: () => {
        goNextStep()
      },
      allowBack: true,
      allowNext: true,
    },]),
    {
      title: 'Summary',
      icon: <AssignmentTurnedIn color="primary" fontSize="large" />,
      type: LoanSubmissionStep.SUMMARY,
      onBack: () => goPrevStep(),
      onNext: () => onSubmit(),
      allowBack: true,
      allowNext: true,
    },
  ];


  return (
    <Layout title={`Edit Stabilized Bridge Loan #${stabilizedBridgeStore.editLoanJson.loanId}`}>
      <Paper className={classes.paper}>
        <>
          <StepsContainer
            disableNavigation={false}
            activeStep={stabilizedBridgeStore.activeStep}
            handleStep={moveToStep}
            steps={loanSubmissionSteps}
          >
            {[
              ...insertIf(showBrokerInformation, [<BrokerInformation />]),
              <BorrowerEntity store={stabilizedBridgeStore} />,
              <BorrowerInformation store={stabilizedBridgeStore} />,
              <PropertyInformationStbBridge
                renderSteps={(modalState, handleAdd, handleEdit) =>
                  <PropertyStepsStabilizedBridge
                    modalState={modalState}
                    handleAdd={handleAdd}
                    handleEdit={handleEdit}
                  />
                }
              />,
              <LoanInformationStabilizedBridge />,
              ...insertIf(marketingPromotionsStore.showPromotions, [<SelectPromotionGrid store={marketingPromotionsStore.marketingPromotionsGridStore} promotionData={{ setPromotion: setPromotion, promotionId: stabilizedBridgeStore.loanInformationStore.getValue('marketingPromotionId') }} />]),
              <Summary />
            ]}
          </StepsContainer>
        </>
      </Paper>
    </Layout>
  );
});
